import { getCirclePickState } from "../../../runtime/circlePickState";
import { getPosPickState } from "../../../runtime/posPickState";
import { getVisualGeomOriginPickState } from "../../../runtime/visualGeomOriginPickState";
import { logger } from "../../../utils/logger";
import { pickScaleSelectionTargetFromIntersects } from "../../../utils/scaleSelection";
import { getSavedSceneCameraPickUuid } from "../../scene/savedCameraMarker";
import { getSavedSceneLightPickUuid } from "../../scene/savedLightMarker";
import { pickBestJointHit, pickJointCenterProxyHit } from "../../scene/scenePicking";
import { getCirclePickHits } from "./circlePickInteraction";
import {
  ensurePosPickMarker,
  updateExpectedBodyPosPickMarker,
  updatePosPickMarkerFromIntersects,
} from "./posPickInteraction";
import { TARGET_HOVER_CURSOR } from "./scenePickerConstants";
import { filterScenePickerIntersects } from "./scenePickerFiltering";
import { clearSceneHoverPreviews } from "./scenePickerHoverPreviews";
import {
  getVisualGeomOriginPickHit,
  updateVisualGeomOriginPickMarker,
} from "./visualGeomOriginPickInteraction";

function hidePosPickMarker(scene) {
  try {
    const marker = ensurePosPickMarker(scene);
    if (marker) marker.visible = false;
  } catch (error) {
    logger.warn("[Scene] Unexpected error during scene update", error);
  }
}

export function processScenePickerPointerMove({
  applyCircleHover,
  canvas,
  camera,
  clearCircleHoverIfActive,
  findValidTarget,
  getCollisionTarget,
  getJointIdFromObject,
  getOwningBodyUuidForObject,
  getResolvedFocusBodiesGateSet,
  getVisualGeomTarget,
  isCollisionGeomObject,
  isOrbitingRef,
  pickConstraintMarkerKey,
  raycaster,
  sceneObjectsRef,
  sceneRef,
  setHoverPreviewBody,
  setHoverPreviewJoint,
  setHoverPreviewMesh,
  showCollision,
  showJoints,
  showSavedCameraMarkers,
  showScaleGizmo,
  showVisual,
  pointer,
  buttons = 0,
  clientX,
  clientY,
  constraintHoveredKeyRef,
}) {
  if (isOrbitingRef.current) return;
  if (
    buttons !== 0 &&
    !getCirclePickState().active &&
    !getVisualGeomOriginPickState().active &&
    !getPosPickState().active
  ) {
    return;
  }

  const clearHoverPreviews = () =>
    clearSceneHoverPreviews({
      setHoverPreviewBody,
      setHoverPreviewJoint,
      setHoverPreviewMesh,
    });

  const rect = canvas.getBoundingClientRect();
  pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
  pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(pointer, camera);

  if (getCirclePickState().active) {
    const circleHits = getCirclePickHits(
      raycaster.intersectObjects(sceneRef.current.children, true)
    );
    if (circleHits.length > 0) {
      applyCircleHover({
        canvas,
        info: circleHits[0].object.userData.info,
        scene: sceneRef.current,
      });
    } else {
      clearCircleHoverIfActive();
      canvas.style.cursor = "default";
    }
    return;
  }

  const visualGeomOriginPickState = getVisualGeomOriginPickState();
  if (visualGeomOriginPickState.active) {
    const hit = getVisualGeomOriginPickHit(
      raycaster.intersectObjects(sceneRef.current.children, true),
      visualGeomOriginPickState
    );
    updateVisualGeomOriginPickMarker({
      ensureMarker: () => ensurePosPickMarker(sceneRef.current),
      hit,
    });
    canvas.style.cursor = hit ? TARGET_HOVER_CURSOR : "crosshair";
    clearHoverPreviews();
    return;
  }

  const posPickState = getPosPickState();
  if (posPickState.active && posPickState.expectedBodyUuid) {
    updateExpectedBodyPosPickMarker({
      ensureMarker: () => ensurePosPickMarker(sceneRef.current),
      expectedBodyUuid: posPickState.expectedBodyUuid,
      raycaster,
      sceneObjectsRef,
    });
    return;
  }

  const markerKey = pickConstraintMarkerKey();
  if (markerKey) {
    constraintHoveredKeyRef.current = markerKey;
    canvas.style.cursor = TARGET_HOVER_CURSOR;
    clearHoverPreviews();
    return;
  }
  constraintHoveredKeyRef.current = null;

  const intersects = raycaster.intersectObjects(sceneRef.current.children, true);
  const focusBodiesGateSet = getResolvedFocusBodiesGateSet();
  const validIntersects = filterScenePickerIntersects(intersects, {
    focusBodiesGateSet,
    getJointIdFromObject,
    getOwningBodyUuidForObject,
    isCollisionGeomObject,
    requireVisualMeshColor: false,
    showCollision,
    showSavedCameraMarkers,
    showVisual,
  });

  const posPickStateForMarker = getPosPickState();
  if (posPickStateForMarker.active) {
    updatePosPickMarkerFromIntersects({
      ensureMarker: () => ensurePosPickMarker(sceneRef.current),
      getOwningBodyUuidForObject,
      posPickState: posPickStateForMarker,
      validIntersects,
    });
  } else {
    hidePosPickMarker(sceneRef.current);
  }

  const savedCameraHoverUuid = showSavedCameraMarkers
    ? validIntersects
        .map((intersect) => getSavedSceneCameraPickUuid(intersect.object))
        .find(Boolean)
    : null;
  const savedLightHoverUuid = validIntersects
    .map((intersect) => getSavedSceneLightPickUuid(intersect.object))
    .find(Boolean);
  if (savedCameraHoverUuid || savedLightHoverUuid) {
    canvas.style.cursor = TARGET_HOVER_CURSOR;
    clearHoverPreviews();
    return;
  }

  if (showScaleGizmo) {
    const scaleTarget = pickScaleSelectionTargetFromIntersects(validIntersects, {
      getCollisionTarget,
      getVisualGeomTarget,
    });
    canvas.style.cursor = scaleTarget ? TARGET_HOVER_CURSOR : "default";
    clearHoverPreviews();
    return;
  }

  if (validIntersects.length === 0) {
    canvas.style.cursor = "default";
    clearHoverPreviews();
    return;
  }

  if (showJoints) {
    const centerJointHover = pickJointCenterProxyHit(validIntersects, findValidTarget);
    if (centerJointHover) {
      canvas.style.cursor = TARGET_HOVER_CURSOR;
      setHoverPreviewJoint(centerJointHover.uuid || centerJointHover.name);
      setHoverPreviewBody(null);
      setHoverPreviewMesh(null);
      return;
    }
  }

  for (const intersect of validIntersects) {
    const target = findValidTarget(intersect.object);
    if (target && target.type !== "joint") {
      canvas.style.cursor = TARGET_HOVER_CURSOR;

      if (target.type === "camera" || target.type === "light") {
        clearHoverPreviews();
        return;
      }

      if (target.type === "body") {
        setHoverPreviewBody(target.uuid);
        setHoverPreviewJoint(null);
      } else if (target.type === "mesh") {
        setHoverPreviewMesh(target.uuid);
        setHoverPreviewBody(null);
        setHoverPreviewJoint(null);
      }
      return;
    }
  }

  if (showJoints) {
    const jointHover = pickBestJointHit(validIntersects, findValidTarget);
    if (jointHover && jointHover.type === "joint") {
      canvas.style.cursor = TARGET_HOVER_CURSOR;
      setHoverPreviewJoint(jointHover.uuid || jointHover.name);
      setHoverPreviewBody(null);
      return;
    }
  }

  canvas.style.cursor = "default";
  clearHoverPreviews();
}
